import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Logo, WordmarkTagline } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { getCart } from "@/lib/utils-cart";

const NAV = [
  { to: "/marketplace", label: "Marketplace" },
  { to: "/stores", label: "Stores" },
  { to: "/services", label: "Business Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/help", label: "Help" },
];

export default function PublicLayout({ children }) {
  const { user, logout, primaryRole } = useAuth();
  const [mobile, setMobile] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    const sync = () => setCartCount(getCart().reduce((s, c) => s + c.quantity, 0));
    sync();
    window.addEventListener("vendora-cart-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("vendora-cart-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="header-home-link" className="flex items-center gap-3">
            <Logo size={32} />
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors hover:text-primary ${isActive ? "text-primary" : "text-muted-foreground"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/cart" data-testid="header-cart-link" className="relative v-btn-ghost">
              <ShoppingBag size={18} />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-secondary text-white text-[10px] font-bold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
            {user ? (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/app" data-testid="header-dashboard-link" className="v-btn-outline">
                  Dashboard
                </Link>
                <button onClick={() => { logout(); nav("/"); }} className="v-btn-ghost text-sm" data-testid="header-logout">
                  Sign out
                </button>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/login" data-testid="header-login-link" className="v-btn-ghost">Sign in</Link>
                <Link to="/register" data-testid="header-register-link" className="v-btn-primary">Get started</Link>
              </div>
            )}
            <button className="lg:hidden v-btn-ghost" onClick={() => setMobile(!mobile)} data-testid="header-mobile-toggle">
              {mobile ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {mobile && (
          <div className="lg:hidden border-t border-border bg-card px-6 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMobile(false)} className="text-sm font-medium text-foreground">
                {n.label}
              </Link>
            ))}
            {!user && (
              <div className="flex gap-2 pt-2">
                <Link to="/login" onClick={() => setMobile(false)} className="v-btn-outline flex-1">Sign in</Link>
                <Link to="/register" onClick={() => setMobile(false)} className="v-btn-primary flex-1">Start</Link>
              </div>
            )}
            {user && (
              <Link to="/app" onClick={() => setMobile(false)} className="v-btn-primary">Open dashboard</Link>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border bg-card mt-16">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <Logo size={40} />
            <p className="mt-4 text-sm text-muted-foreground max-w-sm">
              The commerce operating system for Canadian entrepreneurs. Source from vetted suppliers, sell across channels, ship from one place, and grow.
            </p>
            <div className="mt-5"><WordmarkTagline /></div>
          </div>
          <FooterCol title="Platform" links={[["/marketplace", "Marketplace"], ["/stores", "Storefronts"], ["/services", "Business Services"], ["/pricing", "Pricing"]]} />
          <FooterCol title="Resources" links={[["/help", "Help Center"], ["/faq", "FAQ"], ["/guides", "Guides"], ["/about", "About"]]} />
          <FooterCol title="Legal" links={[["/legal/terms", "Terms"], ["/legal/privacy", "Privacy"], ["/legal/cookies", "Cookies"], ["/legal/refund", "Refund Policy"], ["/legal/shipping", "Shipping Policy"]]} />
        </div>
        <div className="border-t border-border">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>© {new Date().getFullYear()} Vendora Commerce Inc. — Built in Canada.</span>
            <span className="font-mono tracking-wider">v1.0 · CAD · Sandbox-ready</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="label-eyebrow text-primary mb-4">{title}</div>
      <ul className="space-y-2.5">
        {links.map(([to, label]) => (
          <li key={to}>
            <Link to={to} className="text-sm text-muted-foreground hover:text-primary transition-colors">{label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
